import {
  cloneMarketQuery,
  createEmptyMarketQuery,
  firstMarketQueryPage,
  nextMarketQueryPage,
  normalizeMarketQuery
} from './marketQuery';
import { marketQueryFromLegacyRoute } from './marketExplorerRouteAdapter';

function initialState() {
  return {
    draftQuery: createEmptyMarketQuery(),
    appliedQuery: createEmptyMarketQuery(),
    items: [],
    meta: null,
    status: 'idle',
    error: null,
    pageLoading: false,
    persistenceError: null
  };
}

function stockIdentity(stock) {
  return [stock && stock.ticker, stock && stock.direccio, stock && (stock.entry_type_name || stock.entry_type)].join('|');
}

export class MarketExplorerController {
  constructor(client, onChange = () => {}) {
    this.client = client;
    this.onChange = onChange;
    this.state = initialState();
    this.sequence = 0;
    this.initializationSequence = 0;
    this.cancelSource = null;
    this.emit();
  }

  emit() {
    const snapshot = JSON.parse(JSON.stringify(this.state));
    ['error', 'persistenceError'].forEach(name => {
      const error = this.state[name];
      if (error) {
        snapshot[name] = {
          message: error.message || 'Error inesperado.',
          status: error.status || null,
          fields: error.fields || {}
        };
      }
    });
    this.onChange(snapshot);
  }

  setDraft(query) {
    this.state.draftQuery = cloneMarketQuery(query);
    this.emit();
    return this.state.draftQuery;
  }

  resetDraft() {
    return this.setDraft(createEmptyMarketQuery());
  }

  cancelDraft() {
    return this.setDraft(this.state.appliedQuery);
  }

  async initialize(route) {
    const initializationSequence = ++this.initializationSequence;
    this.sequence++;
    if (this.cancelSource) this.cancelSource.cancel('Ruta sustituida por otra más reciente.');
    this.cancelSource = null;
    const adapted = marketQueryFromLegacyRoute(route);
    if (!adapted.handled) return { handled: false };

    let query = adapted.query;
    if (adapted.restoreLast) {
      try {
        const record = await this.client.getLast();
        if (initializationSequence !== this.initializationSequence) return { handled: true, stale: true };
        query = record && record.definition ? normalizeMarketQuery(record.definition) : createEmptyMarketQuery();
      } catch (error) {
        if (initializationSequence !== this.initializationSequence || error.canceled) {
          return { handled: true, stale: true };
        }
        this.state.error = error;
        this.state.status = 'error';
        this.emit();
        throw error;
      }
    }
    if (initializationSequence !== this.initializationSequence) return { handled: true, stale: true };
    const result = await this.apply(query, { persist: false, keepInitialization: true });
    return { handled: true, ...result };
  }

  async apply(query, options = {}) {
    if (!options.keepInitialization) this.initializationSequence++;
    const definition = firstMarketQueryPage(query);
    const sequence = ++this.sequence;
    if (this.cancelSource) this.cancelSource.cancel('Consulta sustituida por otra más reciente.');
    this.cancelSource = this.client.createCancelSource ? this.client.createCancelSource() : null;
    this.state.status = 'loading';
    this.state.error = null;
    this.state.pageLoading = false;
    this.emit();

    try {
      const response = await this.client.execute(definition, this.cancelSource ? { cancelToken: this.cancelSource.token } : {});
      if (sequence !== this.sequence) return { stale: true };
      this.state.appliedQuery = cloneMarketQuery((response.meta && response.meta.applied_query) || definition);
      this.state.draftQuery = cloneMarketQuery(this.state.appliedQuery);
      this.state.items = Array.isArray(response.items) ? response.items : [];
      this.state.meta = response.meta || null;
      this.state.status = this.state.items.length ? 'success' : 'empty';
      this.state.error = null;
      this.emit();

      if (options.persist !== false) {
        try {
          await this.client.saveLast(this.state.appliedQuery);
          if (sequence === this.sequence) this.state.persistenceError = null;
        } catch (error) {
          if (sequence === this.sequence) this.state.persistenceError = error;
        }
        if (sequence === this.sequence) this.emit();
      }
      return { stale: false, response, items: this.state.items };
    } catch (error) {
      if (sequence !== this.sequence || error.canceled) return { stale: true, canceled: true };
      this.state.status = error.status === 403 ? 'restricted' : 'error';
      this.state.error = error;
      this.emit();
      throw error;
    } finally {
      if (sequence === this.sequence) this.cancelSource = null;
    }
  }

  async retry() {
    return this.apply(this.state.appliedQuery, { persist: false });
  }

  async loadNextPage() {
    const pagination = this.state.meta && this.state.meta.pagination;
    if (this.state.pageLoading || !pagination || !pagination.has_more || pagination.next_offset === null) {
      return { skipped: true };
    }

    const sequence = this.sequence;
    const definition = nextMarketQueryPage(this.state.appliedQuery, pagination.next_offset);
    this.state.pageLoading = true;
    this.state.error = null;
    this.emit();
    try {
      const response = await this.client.execute(definition);
      if (sequence !== this.sequence) return { stale: true };
      const seen = new Set(this.state.items.map(stockIdentity));
      const additions = (response.items || []).filter(item => {
        const identity = stockIdentity(item);
        if (seen.has(identity)) return false;
        seen.add(identity);
        return true;
      });
      this.state.items = this.state.items.concat(additions);
      this.state.meta = response.meta || this.state.meta;
      this.state.status = this.state.items.length ? 'success' : 'empty';
      this.state.error = null;
      this.emit();
      return { stale: false, response, additions };
    } catch (error) {
      if (sequence !== this.sequence || error.canceled) return { stale: true };
      this.state.error = error;
      this.emit();
      throw error;
    } finally {
      if (sequence === this.sequence) {
        this.state.pageLoading = false;
        this.emit();
      }
    }
  }

  async saveNamed(name) {
    const trimmed = String(name || '').trim();
    if (!trimmed) throw new Error('El nombre de la búsqueda es obligatorio.');
    return this.client.saveNamed(trimmed, firstMarketQueryPage(this.state.appliedQuery));
  }

  destroy() {
    this.sequence++;
    this.initializationSequence++;
    if (this.cancelSource) this.cancelSource.cancel('Explorador destruido.');
    this.cancelSource = null;
  }
}
